'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Anchor, Route } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { CompanyStatisticsListingView, useCompanyStatisticsListingClick } from '@/components/analytics/CompanyStatisticsTracker';
import { searchClientDetailed, type SearchSuggestion } from '@/lib/api/public/search';

type SearchVariant = 'light' | 'dark';
type SearchSize = 'default' | 'compact';

interface SearchAutocompleteProps {
  placeholder?: string;
  variant?: SearchVariant;
  size?: SearchSize;
}

const variantStyles = {
  light: {
    container:
      'bg-gray-50 border-gray-300 shadow-sm hover:border-gray-400 focus-within:border-[var(--ra-green)] focus-within:ring-2 focus-within:ring-[var(--ra-green)]/20 focus-within:bg-white',
    input: 'text-gray-900 placeholder-gray-500',
    icon: 'text-gray-500',
  },
  dark: {
    container:
      'bg-white/10 border-white/20 backdrop-blur-md focus-within:border-white/40 focus-within:bg-white/15',
    input: 'text-white placeholder-white/60',
    icon: 'text-white/60',
  },
};

// Type badge colors and icons
const typeConfig: Record<
  string,
  { icon: JSX.Element; badge: string; label: string }
> = {
  provider: {
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
    badge: 'bg-purple-100 text-purple-700',
    label: 'Provider',
  },
  route: {
    icon: <Route className="w-5 h-5" />,
    badge: 'bg-blue-100 text-blue-700',
    label: 'Route',
  },
  port: {
    icon: <Anchor className="w-5 h-5" />,
    badge: 'bg-cyan-100 text-cyan-700',
    label: 'Port',
  },
  geo_object: {
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    badge: 'bg-green-100 text-green-700',
    label: 'Region',
  },
  country: {
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
        />
      </svg>
    ),
    badge: 'bg-amber-100 text-amber-700',
    label: 'Country',
  },
  hub_page: {
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
    badge: 'bg-gray-100 text-gray-700',
    label: 'Page',
  },
};

export const SearchAutocomplete = ({
  placeholder = 'Search countries, routes, ports, or providers...',
  variant = 'dark',
  size = 'default',
}: SearchAutocompleteProps) => {
  const router = useRouter();
  const recordListingClick = useCompanyStatisticsListingClick();
  const styles = variantStyles[variant];
  const isCompact = size === 'compact';
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [status, setStatus] = useState<'success' | 'partial' | 'error'>('success');
  const [composing, setComposing] = useState(false);
  const [retry, setRetry] = useState(0);
  const queryRef = useRef('');
  const dismissedRef = useRef(false);
  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const resultsQueryRef = useRef<string | null>(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    // Remove retired identifiers without reading their values or contacting an endpoint.
    try {
      localStorage.removeItem('ra-search-browser');
    } catch {
      // Storage may be disabled; search does not depend on it.
    }
    try {
      sessionStorage.removeItem('ra-search-session');
    } catch {
      // Storage may be disabled; search does not depend on it.
    }
  }, []);

  useEffect(() => {
    if (composing || debouncedQuery.length < 2 || debouncedQuery !== queryRef.current) {
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const generation = ++generationRef.current;
    setIsLoading(true);
    void searchClientDetailed({ q: debouncedQuery, limit: 10 }, controller.signal).then((result) => {
      if (controller.signal.aborted || generation !== generationRef.current || queryRef.current !== debouncedQuery) {
        return;
      }
      resultsQueryRef.current = debouncedQuery;
      setSuggestions(result.suggestions);
      setStatus(result.status);
      setIsLoading(false);
      setIsOpen(!dismissedRef.current && !!containerRef.current?.contains(document.activeElement));
    }).catch(() => {
      // Superseded requests are intentionally cancelled; other errors become detailed outcomes.
    });
    return () => { controller.abort(); };
  }, [debouncedQuery, composing, retry]);

  const invalidateResults = useCallback(() => {
    ++generationRef.current;
    abortRef.current?.abort();
    resultsQueryRef.current = null;
    setSuggestions([]);
    setSelectedIndex(-1);
  }, []);

  useEffect(() => {
    const outside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        dismissedRef.current = true;
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', outside);
    return () => { document.removeEventListener('mousedown', outside); };
  }, []);

  const handleSelect = useCallback((suggestion: SearchSuggestion) => {
    if (resultsQueryRef.current !== queryRef.current) {
      return;
    }
    if (suggestion.type === 'provider' && suggestion.url) {
      recordListingClick(suggestion.statistics, 'search_suggestion');
    }
    dismissedRef.current = true;
    invalidateResults();
    setIsOpen(false);
    setIsLoading(false);
    queryRef.current = '';
    setQuery('');
    if (suggestion.url) {
      router.push(suggestion.url);
    }
  }, [router, invalidateResults, recordListingClick]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.nativeEvent.isComposing) {
      return;
    }
    if (event.key === 'Escape') {
      dismissedRef.current = true;
      setIsOpen(false);
      setSelectedIndex(-1);
      return;
    }
    if (!isOpen || suggestions.length === 0) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((previous) => { return Math.min(previous + 1, suggestions.length - 1); });
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((previous) => { return Math.max(previous - 1, 0); });
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
    }
  }, [isOpen, suggestions, selectedIndex, handleSelect]);

  // Get config for suggestion type, using anchor icon for port-related hub pages
  const getTypeConfig = (type: string, suggestion?: SearchSuggestion) => {
    if (type === 'hub_page' && suggestion?.url?.includes('/ports/')) {
      return { ...typeConfig.hub_page, icon: <Anchor className="w-5 h-5" /> };
    }
    return typeConfig[type] || typeConfig.hub_page;
  };

  return (
    <div ref={containerRef} className="relative w-full z-50 ph-no-capture"
      onBlur={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) {
          dismissedRef.current = true;
          setIsOpen(false);
        }
      }}
    >
      <div
        className={`relative flex items-center ${
          isCompact ? 'rounded-lg' : 'rounded-xl'
        } border transition-all ${styles.container}`}
      >
        {/* Search Icon */}
        <div
          className={`absolute ${isCompact ? 'left-3' : 'left-4'} ${
            styles.icon
          }`}
        >
          {isLoading ? (
            <svg
              className={
                isCompact ? 'w-4 h-4 animate-spin' : 'w-5 h-5 animate-spin'
              }
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              className={isCompact ? 'w-4 h-4' : 'w-5 h-5'}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            const value = e.target.value;
            queryRef.current = value;
            dismissedRef.current = false;
            invalidateResults();
            setQuery(value);
            setIsLoading(value.length >= 2);
            setIsOpen(value.length >= 2);
          }}
          onFocus={() => {
            dismissedRef.current = false;
            if (resultsQueryRef.current === queryRef.current) {
              setIsOpen(true);
            }
          }}
          onCompositionStart={() => {
            setComposing(true);
            invalidateResults();
          }}
          onCompositionEnd={() => {
            setComposing(false);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full bg-transparent focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 ${
            isCompact
              ? 'min-w-0 px-3 py-2.5 pl-9 pr-12 text-base md:py-2 md:pr-10 md:text-sm'
              : 'min-w-0 px-4 py-4 pl-12 pr-14 text-base'
          } ${styles.input}`}
          aria-label="Search"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />

        {/* Clear button */}
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            onMouseDown={(event) => { event.preventDefault(); }}
            onClick={() => {
              dismissedRef.current = true;
              queryRef.current = '';
              invalidateResults();
              setQuery('');
              setIsLoading(false);
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            className={`absolute ${
              isCompact ? 'right-0 md:right-1 md:h-8 md:w-8' : 'right-2'
            } inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-black/10 transition-colors ${
              styles.icon
            }`}
          >
            <svg
              className={isCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <ul
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[min(20rem,45dvh)] overflow-y-auto"
          role="listbox"
        >
          {suggestions.map((suggestion, index) => {
            const config = getTypeConfig(suggestion.type, suggestion);
            return (
              <li
                key={`${suggestion.type}-${suggestion.url}-${index}`}
                data-company-statistics-listing
                role="option"
                aria-selected={selectedIndex === index}
                className={`cursor-pointer transition-colors ${
                  selectedIndex === index
                    ? 'bg-[var(--ra-green)]/10'
                    : 'hover:bg-gray-50'
                }`}
                onMouseDown={(event) => {
                  if (event.button === 0) {
                    event.preventDefault();
                  }
                }}
                onMouseEnter={() => { setSelectedIndex(index); }}
              >
                <a href={suggestion.url || undefined} className="flex items-center gap-3 px-4 py-3" tabIndex={-1} onClick={(event) => {
                  if (event.button !== 0) {
                    return;
                  }
                  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
                    if (suggestion.type === 'provider') {
                      recordListingClick(suggestion.statistics, 'search_suggestion');
                    }
                    return;
                  }
                  event.preventDefault();
                  handleSelect(suggestion);
                }} onAuxClick={(event) => {
                  if (event.button === 1 && suggestion.type === 'provider') {
                    recordListingClick(suggestion.statistics, 'search_suggestion');
                  }
                }}>
                {suggestion.type === 'provider' && <CompanyStatisticsListingView attribution={suggestion.statistics} surface="search_suggestion" />}
                <div className="flex-shrink-0 text-gray-400">{config.icon}</div>
                <div className="min-w-0 flex-1 text-left">
                  <span className="block truncate font-medium text-gray-900">
                    {suggestion.label}
                  </span>
                  <span className="block truncate text-xs text-gray-500">
                    {suggestion.subtitle || config.label}
                  </span>
                </div>
                </a>
              </li>
            );
          })}
        </ul>
      )}

      {/* No results message */}
      {isOpen &&
        query.length >= 2 &&
        suggestions.length === 0 &&
        !isLoading && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <p className="text-gray-600 font-medium"><span>{status === 'success' ? 'No results found' : 'Search temporarily unavailable'}</span></p>
            <p className="text-sm text-gray-400 mt-1">
              <span>{status === 'success' ? 'Try a different search term' : 'Please try again in a moment'}</span>
            </p>
            {status !== 'success' && (
              <button type="button" className="mt-3 text-sm font-medium text-green-700" onClick={() => {
                setRetry((value) => { return value + 1; });
              }}><span>Retry search</span></button>
            )}
          </div>
        )}
    </div>
  );
};
